import React, { useMemo } from "react";
import { Row } from "./Row/Row";
import { ResultsInIndex } from "./ResultsInIndex/ResultsInIndex";
import { Highlight } from "./Highlight/Highlight";
import { useNormalisedHits } from "@/features/search/components/Results/hooks/useNormalisedHits";
import type { CasinoGameHit } from "@/features/search/components/Results/models/casinoGameHit";
import { getGamePath } from "@/features/casino/utils/helpers";
import { CategoryIcon } from "@/features/casino/components/CategoryIcon";
import { useConfigure } from "react-instantsearch";
import { useCasinoCategoryCounts } from "@/features/casino/hooks/useCasinoCategoryCounts";
import * as styles from "@/features/search/components/Results/Results.module.scss";

const getFilters = (indexName: string, country: string) => {
  const filters = [];
  if (indexName === "casino_games" && country) {
    filters.push(`allowedCountries:${country}`);
  }

  return filters.join(" AND ");
};

const CasinoGamesResults = () => {
  const { items, results } = useNormalisedHits<CasinoGameHit>();
  const indexName = "casino_games";
  const { country } = useCasinoCategoryCounts();
  const filters = useMemo(() => {
    return getFilters(indexName, country || "");
  }, [indexName, country]);

  useConfigure({
    filters,
  });

  return (
    <ResultsInIndex
      indexName={`Casino Games`}
      total={results?.nbHits || 0}
      pages={results?.nbPages || 0}
      perPage={results?.hitsPerPage || 0}
    >
      {(items || []).map((item) => {
        const titleHighlight = item.normalisedHighlights?.title?.[0];
        const providerHighlight = item.normalisedHighlights?.productName?.[0];
        const categoryHighlight = item.normalisedHighlights?.categoryLabel?.[0];

        return (
          <Row
            key={item.objectID}
            iconClassName={styles.casinoGameImageContainer}
            icon={
              <div
                className={styles.casinoGameImage}
                style={{
                  backgroundImage: `url("${item.icon}")`,
                }}
              />
            }
            title={
              <>
                <div className={styles.casinoCategoryIconContainer}>
                  <CategoryIcon iconUrl={item.categoryIconUrl} size={10} />
                </div>
                <Highlight
                  text={item.title}
                  matchedWords={titleHighlight?.matchedWords || []}
                  html={titleHighlight?.value || ""}
                />
              </>
            }
            subTitle={
              <>
                <div>
                  <Highlight
                    className={styles.inline}
                    text={item.categoryLabel}
                    matchedWords={categoryHighlight?.matchedWords || []}
                    html={categoryHighlight?.value || ""}
                  />
                  {` by `}
                  <Highlight
                    className={styles.inline}
                    text={item.productName}
                    matchedWords={providerHighlight?.matchedWords || []}
                    html={providerHighlight?.value || ""}
                  />
                </div>
              </>
            }
            to={getGamePath(item.slug)}
          />
        );
      })}
    </ResultsInIndex>
  );
};

export { CasinoGamesResults };
